import React from 'react';

const LoadingSpinner = ({ size = 'default' }) => {
  const sizeClasses = {
    small: 'w-4 h-4',
    default: 'w-8 h-8', 
    large: 'w-12 h-12',
    xl: 'w-16 h-16'
  };

  return (
    <div className={`${sizeClasses[size]} relative`}>
      <div className="absolute inset-0 rounded-full border-4 border-blue-200 opacity-25"></div>
      <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-blue-600 animate-spin"></div>
    </div>
  );
};

const LoadingOverlay = ({ 
  show, 
  message = 'Loading transactions...', 
  subMessage = 'Please wait while we fetch your data',
  className = '' 
}) => {
  if (!show) return null;

  return (
    <div 
      className={`fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center ${className}`}
      style={{ backdropFilter: 'blur(8px)' }}
    >
      <div className="flex flex-col items-center space-y-6 p-8 bg-white rounded-2xl shadow-xl border border-gray-100 max-w-md mx-4">
        {/* Modern Spinner */}
        <div className="relative">
          {/* Outer Ring */}
          <div className="w-16 h-16 border-4 border-gray-200 rounded-full"></div>
          {/* Spinning Ring */}
          <div className="absolute top-0 left-0 w-16 h-16 border-4 border-transparent border-t-blue-600 rounded-full animate-spin"></div>
          {/* Inner Dot */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
        </div>

        {/* Loading Text */}
        <div className="text-center space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">
            {message}
          </h3>
          <p className="text-sm text-gray-600">
            {subMessage}
          </p>
        </div>

        {/* Progress Dots */}
        <div className="flex space-x-1">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"
              style={{
                animationDelay: `${i * 0.3}s`,
                animationDuration: '1.5s'
              }}
            ></div>
          ))}
        </div>
      </div>
    </div>
  );
};

const TableLoadingOverlay = ({ show, message = 'Loading...', rowCount = 5 }) => {
  if (!show) return null;

  return (
    <div className="relative">
      {/* Loading Overlay for Table */}
      <div className="absolute inset-0 bg-white/90 backdrop-blur-sm z-10 flex items-center justify-center">
        <div className="flex items-center space-x-3 px-6 py-4 bg-white rounded-lg shadow-lg border">
          <LoadingSpinner size="default" />
          <span className="text-gray-700 font-medium">{message}</span>
        </div>
      </div>
      
      {/* Skeleton Content */}
      <div className="space-y-3 p-4">
        {[...Array(rowCount)].map((_, i) => (
          <div key={i} className="animate-pulse flex space-x-4">
            <div className="h-4 bg-gray-200 rounded flex-1"></div>
            <div className="h-4 bg-gray-200 rounded w-20"></div>
            <div className="h-4 bg-gray-200 rounded w-16"></div>
            <div className="h-4 bg-gray-200 rounded w-24"></div>
          </div>
        ))}
      </div>
    </div>
  );
};

const InlineLoader = ({ 
  show, 
  message = 'Loading...', 
  size = 'default',
  className = ''
}) => {
  if (!show) return null;

  return (
    <div className={`flex items-center justify-center space-x-3 p-4 ${className}`}>
      <LoadingSpinner size={size} />
      <span className="text-gray-600 font-medium">{message}</span>
    </div>
  );
};

export { LoadingOverlay, LoadingSpinner, TableLoadingOverlay, InlineLoader };
export default LoadingOverlay;